/* eslint-disable prefer-template */

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select,
  Space,
  Form,
  Button,
  notification,
  Radio,
  Row,
  Col,
  Badge,
  DatePicker,
  Checkbox,
} from 'antd';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faSpinner} from '@fortawesome/free-solid-svg-icons';

import numeral from 'numeral';
import * as grin4uService from '../../services/grin4u';

import styles from './index.module.scss';
import stylesForm from '../../common/form.module.scss';
import Loading from '../../components/Loading';
import SubscriptionsTable from '../../components/SubscriptionsTable';
import {formatMoneyFromFleet} from '../../helpers/utils';

const CustomerSubscriptionsModule = ({
  customer,
  country,
  selectedFleet,
  // eslint-disable-next-line no-unused-vars
  user,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showFormCreate, setShowFormCreate] = useState(false);
  const [defineSubscriptionDates, setDefineSubscriptionDates] = useState(false);
  const [usedFields, setUsedFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [checkingReferral, setCheckingReferral] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [referralDiscount, setReferralDiscount] = useState(null);

  const createSubscriptionEnabled = customer.grin4u_status === 'ACTIVE';

  const loadSubscriptions = async () => {
    setLoading(true);
    const rsp = await grin4uService.getUserSubscriptions(customer.id);

    if (rsp?.status) {
      setUserSubscriptions(rsp.data);
    } else {
      setUserSubscriptions([]);
    }
    setLoading(false);
    return true;
  };

  const calculateBreakdown = () => {
    const unitPrice =
      selectedFleet.cost?.grin4u?.plans[usedFields.item_type]?.find(
        (plan) => plan.value === usedFields.plan,
      )?.price || 0;
    const {qty} = usedFields;
    const subtotal = unitPrice * qty;
    const discountReason = usedFields.discount_reason;
    const discountDescription = usedFields.discount_description;
    const fieldDiscountRaw = parseFloat(
      String(usedFields.discount || 0).replace(',', '.'),
    );
    const fieldDiscount = !Number.isNaN(fieldDiscountRaw)
      ? +fieldDiscountRaw.toFixed(2)
      : 0;

    const discount = Math.min(Math.max(fieldDiscount, 0), subtotal);

    const additionalQty = 1;
    const additionalUnitPriceRaw = parseFloat(
      String(usedFields.additional_unit_price || 0).replace(',', '.'),
    );
    const additionalUnitPrice = !Number.isNaN(additionalUnitPriceRaw)
      ? additionalUnitPriceRaw
      : 0;

    const additional = additionalQty * additionalUnitPrice;
    const total = Math.max(
      subtotal - discount - (referralDiscount?.amount || 0) + additional,
      0,
    );
    return {
      qty,
      unitPrice,
      subtotal,
      referralDiscount,
      discount,
      discountReason,
      discountDescription,
      additional,
      total,
    };
  };

  const onFinish = async (values) => {
    if (!createSubscriptionEnabled) {
      return null;
    }

    const selectedPlanData = selectedFleet.cost?.grin4u?.plans[
      usedFields.item_type
    ]?.find((plan) => plan.value === usedFields.plan)?.data;
    const breakdown = calculateBreakdown();
    const durationDays = parseInt(values.plan.split('-')[0], 10);
    const payload = {
      type: `PLAN_${durationDays}`,
      item_type: values.item_type,
      total: breakdown.total,
      qty: values.qty,
      fleet_id: selectedFleet.id,
      user_id: customer.id,
      country,
      data: {
        breakdown: {
          ...breakdown,
          additionals: [
            values.additional_item &&
              values.additional_qty > 0 && {
                item_type: values.additional_item,
                qty: values.additional_qty,
                unit_price: values.additional_unit_price,
                total: values.additional_total,
              },
          ].filter((x) => !!x),
        },
        plan: selectedPlanData,

        duration_days: durationDays,
        shipping_point: values.shipping_point,
      },
    };

    if (defineSubscriptionDates) {
      if (usedFields.paid_at > usedFields.start_at) {
        notification.error({
          message:
            'La fecha de pago de la suscripción no puede ser posterior a la de inicio',
          placement: 'top',
          key: 'result',
        });
        return false;
      }
      if (usedFields.end_at < usedFields.start_at) {
        notification.error({
          message:
            'La fecha de fin de la suscripción no puede ser anterior a la de inicio',
          placement: 'top',
          key: 'result',
        });
        return false;
      }
      payload.start_at = usedFields.start_at.format('YYYY-MM-DD');
      payload.end_at = usedFields.end_at.format('YYYY-MM-DD');
      payload.paid_at = usedFields.paid_at.format('YYYY-MM-DD');
    }

    notification.destroy('in_progress');
    notification.destroy('result');
    notification.info({
      message: 'Creando suscripción...',
      placement: 'top',
      key: 'result',
      duration: 0,
    });

    const rsp = await grin4uService.createSubscription(payload);

    if (rsp?.status) {
      notification.success({
        message: 'Suscripción creada.',
        placement: 'top',
        key: 'result',
      });
      loadSubscriptions();
      setShowFormCreate(false);
    } else {
      notification.error({
        message: 'Error al crear suscripción.',
        placement: 'top',
        key: 'result',
      });
    }
    setSaving(false);
    return true;
  };

  const onFinishFailed = () => {
    // console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    loadSubscriptions();
  }, [customer.id]);

  const checkReferralCode = async () => {
    setCheckingReferral(true);
    const referralCode = form.getFieldValue('referralCode');
    const rsp = await grin4uService.checkReferralCode(
      customer.id,
      referralCode,
    );
    if (rsp?.status) {
      const amount = selectedFleet.cost?.grin4u?.referral_discount.referred;
      setReferralDiscount({
        ...rsp.data,
        amount,
      });
    } else {
      setReferralDiscount(null);
      notification.error({
        message: 'El código de referido no es válido.',
        placement: 'top',
        key: 'result',
      });
    }
    setCheckingReferral(false);
  };

  const fieldItemType = usedFields.item_type;
  const breakdown = calculateBreakdown();
  const {total} = breakdown;

  const discountItems = [
    {
      label: 'Ninguno',
      value: '',
      discount: 0,
    },
    {
      label: 'Club Intercorp (15%)',
      value: 'intercorp',
      discount: 15,
    },
    {
      label: 'Cuenta Sueldo Interbank (15%)',
      value: 'interbank',
      discount: 15,
    },
    {
      label: 'CMR Falabella (20%)',
      value: 'cmr-falabella',
      discount: 20,
    },
    {
      label: 'Otro',
      value: 'other',
      discount: 0,
    },
  ];

  const selectedPlan = selectedFleet.cost?.grin4u?.plans[
    usedFields.item_type
  ]?.find((plan) => plan.value === usedFields.plan);
  const totalPrice = (selectedPlan?.price ?? 0) * (usedFields.qty ?? 1);
  return (
    <div className={styles.cnt}>
      {createSubscriptionEnabled && (
        <Button
          onClick={() => {
            form.resetFields();
            setShowFormCreate((s) => !s);
          }}
          type='secondary'
        >
          <FontAwesomeIcon icon={faPlus} style={{marginRight: 5}} /> Crear
          Suscripción
        </Button>
      )}
      {!createSubscriptionEnabled && (
        <div
          style={{
            marginTop: 40,
            fontWeight: 'bold',
            color: '#c30',
            textAlign: 'center',
          }}
        >
          * No puedes crear suscripciones porque el cliente no tiene todos los
          datos completos
        </div>
      )}
      {showFormCreate && (
        <div className={[styles.createForm, stylesForm.fields].join(' ')}>
          <Form
            name='basic'
            shouldUpdate
            form={form}
            initialValues={{
              qty: 1,
              referralCode: '',
              discount: 0,
              discount_reason: '',
              discount_description: '',
              additional_item: '',
              additional_qty: 1,
              additional_unit_price: 0,
              additional_total: 0,
            }}
            layout='vertical'
            onValuesChange={(x, allValues) => {
              setUsedFields(allValues);
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            size='large'
          >
            <Row gutter={24}>
              <Col sm={6} xs={24}>
                <Form.Item
                  label='Tipo de patín'
                  name='item_type'
                  rules={[
                    {required: true, message: 'El Tipo de patín es requerido'},
                  ]}
                >
                  <Radio.Group>
                    <Radio value='SKICK'>SKICK</Radio>
                    <Radio value='SMAX'>SMAX</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col sm={4} xs={24}>
                <Form.Item
                  label='Cantidad'
                  name='qty'
                  rules={[
                    {required: true, message: 'La cantidad es requerida'},
                  ]}
                >
                  <Input placeholder='' type='number' min={1} />
                </Form.Item>
              </Col>
              <Col sm={14} xs={24}>
                <Form.Item
                  label='Plan'
                  name='plan'
                  rules={[{required: true, message: 'El Plan es requerido'}]}
                >
                  <Select
                    options={
                      fieldItemType
                        ? selectedFleet.cost?.grin4u?.plans[fieldItemType]
                            .filter((x) => x.current)
                            .sort(
                              (a, b) =>
                                a.price -
                                a.current * 10000 -
                                (b.price - b.current * 10000),
                            )
                            .map((x) => ({
                              ...x,
                              label: (
                                <>
                                  <Badge
                                    count={x.current ? 'Actual' : 'Anterior'}
                                    color={x.current ? '#090' : '#c30'}
                                    style={{marginRight: 5}}
                                  />
                                  {x.label +
                                    ' - ' +
                                    formatMoneyFromFleet(
                                      x.price,
                                      selectedFleet,
                                    )}
                                </>
                              ),
                            }))
                        : []
                    }
                    onChange={(value) => {
                      if (usedFields.discount_reason) {
                        const newSelectedPlan =
                          selectedFleet.cost?.grin4u?.plans[
                            usedFields.item_type
                          ]?.find((plan) => plan.value === value);
                        const selectedDiscount = discountItems.find(
                          (x) => x.value === usedFields.discount_reason,
                        );
                        const newFields = {
                          discount: (
                            (selectedDiscount.discount *
                              newSelectedPlan.price) /
                            100
                          ).toFixed(2),
                        };
                        Object.keys(newFields).forEach((key) => {
                          form.setFieldValue(key, newFields[key]);
                        });
                        setUsedFields((prev) => ({...prev, ...newFields}));
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col sm={12} xs={24}>
                <Form.Item label='Código de Referido' name='referralCode'>
                  <Space>
                    <Input
                      placeholder=''
                      min={0}
                      onFocus={(e) => e.target.select()}
                    />
                    <Button onClick={checkReferralCode}>
                      {checkingReferral && (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      )}
                      {!checkingReferral && <>Verificar código</>}
                    </Button>
                  </Space>
                </Form.Item>
                {referralDiscount ? (
                  <div style={{marginBottom: 20, fontWeight: 'bold'}}>
                    Descuento por referido:{' '}
                    {formatMoneyFromFleet(
                      referralDiscount.amount,
                      selectedFleet,
                    )}
                  </div>
                ) : (
                  ''
                )}
              </Col>
            </Row>
            {usedFields.plan && (
              <div>
                <Row gutter={24}>
                  <Col sm={12} xs={24}>
                    <div className='flex flex-col gap-2'>
                      <Form.Item
                        label='Motivo del descuento'
                        name='discount_reason'
                      >
                        <Select
                          options={discountItems}
                          onChange={(value) => {
                            const selectedDiscount = discountItems.find(
                              (x) => x.value === value,
                            );
                            const newFields = {
                              discount: (
                                (selectedDiscount.discount *
                                  selectedPlan.price) /
                                100
                              ).toFixed(2),
                              discount_reason: value,
                            };
                            Object.keys(newFields).forEach((key) => {
                              form.setFieldValue(key, newFields[key]);
                            });
                            setUsedFields((prev) => ({...prev, ...newFields}));
                          }}
                        />
                      </Form.Item>
                      {usedFields.discount_reason === 'other' && (
                        <Form.Item name='discount_description'>
                          <Input
                            placeholder='Detalle del descuento...'
                            type='text'
                            onFocus={(e) => e.target.select()}
                          />
                        </Form.Item>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xs={24}>
                    <Form.Item label='Descuento' name='discount'>
                      <Input
                        placeholder=''
                        type='text'
                        disabled={
                          !usedFields.discount_reason ||
                          usedFields.discount_reason !== 'other'
                        }
                        onChange={(e) => {
                          const {value} = e.target;
                          const normalizedValue = value.replace(',', '.');
                          const numericValue = Number(normalizedValue);

                          if (
                            value !== '' &&
                            normalizedValue !== '.' &&
                            Number.isNaN(numericValue)
                          ) {
                            return;
                          }

                          if (numericValue > totalPrice) {
                            form.setFieldValue('discount', totalPrice);
                            setUsedFields((prev) => ({
                              ...prev,
                              discount: totalPrice,
                            }));
                          } else {
                            form.setFieldValue('discount', value);
                            setUsedFields((prev) => ({
                              ...prev,
                              discount: value,
                            }));
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                      />
                      {usedFields.discount &&
                      Number(form.getFieldValue('discount')) > totalPrice ? (
                        <div style={{color: 'red'}}>
                          {form.getFieldValue('discount')}
                          El descuento no puede ser mayor al precio del plan
                        </div>
                      ) : null}
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24} className='mb-4'>
                  <Col sm={12} xs={24}>
                    <Form.Item label='Adicional' name='additional_item'>
                      <Select
                        options={[
                          {label: 'Ninguno', value: ''},
                          {label: 'Candado', value: 'candado'},
                        ]}
                        onChange={(value) => {
                          let newFields = {
                            additional_qty: 0,
                            additional_unit_price: 0,
                            additional_total_price: 0,
                          };
                          if (value === 'candado') {
                            const pricesByDays = {
                              30: 10,
                              60: 14,
                              90: 21,
                            };
                            const days = selectedPlan?.data?.days ?? 0;
                            const price =
                              days in pricesByDays ? pricesByDays[days] : 0;
                            newFields = {
                              additional_qty: 1,
                              additional_unit_price: price,
                              additional_total_price: price * 1,
                            };
                          } else {
                            newFields = {
                              additional_qty: 0,
                              additional_unit_price: 0,
                              additional_total_price: 0,
                            };
                          }
                          Object.keys(newFields).forEach((key) => {
                            form.setFieldValue(key, newFields[key]);
                          });

                          setUsedFields((prev) => ({
                            ...prev,
                            ...newFields,
                          }));
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={12} xs={24}>
                    <div className='flex gap-4 items-start'>
                      <Form.Item label='Cantidad' name='additional_qty'>
                        <Input
                          placeholder=''
                          type=''
                          min={0}
                          disabled={
                            !usedFields.additional_item ||
                            usedFields.additional_item === ''
                          }
                          onFocus={(e) => e.target.select()}
                        />
                      </Form.Item>
                      <Form.Item
                        label='Precio unitario'
                        name='additional_unit_price'
                        className='grow'
                      >
                        <Input
                          placeholder=''
                          type=''
                          min={0}
                          disabled={
                            !usedFields.additional_item ||
                            usedFields.additional_item === ''
                          }
                          onFocus={(e) => e.target.select()}
                        />
                      </Form.Item>
                      <Form.Item
                        label='Total'
                        name='additional_total_price'
                        className='grow'
                      >
                        <div className='min-w-[80px]'>
                          {selectedFleet.cost?.currency_symbol}{' '}
                          {numeral(
                            form.getFieldValue('additional_qty') *
                              form.getFieldValue('additional_unit_price'),
                          ).format(selectedFleet.cost?.currency_format)}
                        </div>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            <Row gutter={24}>
              <Col sm={24} xs={24}>
                <Form.Item
                  label='Punto de entrega'
                  name='shipping_point'
                  rules={[
                    {
                      required: true,
                      message: 'El Punto de entrega es requerido',
                    },
                  ]}
                >
                  <Radio.Group>
                    <Space direction='vertical'>
                      <Radio value='house'>
                        Domicilio - {customer.data?.grin4uinfo.house_address},{' '}
                        {customer.data?.grin4uinfo.house_district}
                      </Radio>

                      <Radio
                        value='work'
                        disabled={!customer.data?.documents.cert_work}
                      >
                        Trabajo - {customer.data?.grin4uinfo.work_address},{' '}
                        {customer.data?.grin4uinfo.work_district}
                        {!customer.data?.documents.cert_work && (
                          <span>
                            {' '}
                            * Para poder entregar en el trabajo es necesario{' '}
                            completar el certificado de trabajo
                          </span>
                        )}
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col sm={24} xs={24}>
                <Form.Item label='Total a pagar' name='total'>
                  {formatMoneyFromFleet(total, selectedFleet)}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24} style={{marginBottom: 20}}>
              <Col sm={24} xs={24}>
                <Checkbox
                  defaultChecked={!!defineSubscriptionDates}
                  onChange={(e) => {
                    form.setFieldValue('paid_at', null);
                    form.setFieldValue('start_at', null);
                    form.setFieldValue('end_at', null);
                    setDefineSubscriptionDates(e.target.checked);
                  }}
                >
                  Definir fechas de la suscripción (solo para suscripciones
                  antiguas)
                </Checkbox>
                <br />
              </Col>
            </Row>
            {!!defineSubscriptionDates && (
              <>
                <br />
                <strong style={{color: '#f60'}}>
                  * Al crear esta suscripción con fechas quedará registrada como
                  pagada y activa
                </strong>
                <br />
                <br />
                <Row gutter={24}>
                  <Col sm={8} xs={24}>
                    <Form.Item
                      label='Fecha de pago de la suscripción'
                      name='paid_at'
                    >
                      <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                  </Col>
                  <Col sm={8} xs={24}>
                    <Form.Item
                      label='Fecha de inicio de la suscripción'
                      name='start_at'
                    >
                      <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                  </Col>
                  <Col sm={8} xs={24}>
                    <Form.Item
                      label='Fecha de fin de la suscripción'
                      name='end_at'
                    >
                      <DatePicker style={{width: '100%'}} />
                    </Form.Item>
                  </Col>
                </Row>
                <br />
                <br />
              </>
            )}
            <Form.Item>
              <Button
                type='primary'
                htmlType='submit'
                disabled={
                  saving ||
                  (usedFields.discount_reason === 'other' &&
                    !usedFields.discount_description) ||
                  usedFields.discount > totalPrice
                }
              >
                Guardar
              </Button>
              <Button
                type='link'
                style={{color: '#f30'}}
                onClick={() => setShowFormCreate(false)}
                disabled={saving}
              >
                Cerrar
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}
      {createSubscriptionEnabled && (
        <div>
          {loading ? (
            <Loading />
          ) : (
            <div className={styles.subscriptions}>
              <SubscriptionsTable
                fleet={selectedFleet}
                subscriptions={userSubscriptions}
                show={['status']}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
CustomerSubscriptionsModule.propTypes = {
  selectedFleet: PropTypes.object.isRequired,
  customer: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
};
export default CustomerSubscriptionsModule;
